<template>
	<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
		<CallFlowModal
			v-if="selectedSIPCapture"
			:call-id="selectedSIPCapture"
			:call-data="selectedCallData"
			refresh-interval="5000"
			:realtime="true"
			@close="selectedSIPCapture = null"
		/>
		<CIPFlowModal
			v-if="selectedFlow"
			:call-data="selectedFlowData"
			:completed="selectedFlowIsCompleted"
			refresh-interval="1000"
			:readonly="true"
			@close="selectedFlow = null"
		/>
		<div class="row hideMobile">
			<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12 tableOverflow">
				<div class="listHeader">
					<div class="row">
						<div class="col-xs-12 col-sm-2 col-md-2">
							<p class="cursor-pointer" @click="sortBy('startTime')">
								Start Time <span :class="getSortIcon('startTime')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<p class="cursor-pointer" @click="sortBy('accountName')">
								Account <span :class="getSortIcon('accountName')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<p class="cursor-pointer" @click="sortBy('callFrom')">
								Call From <span :class="getSortIcon('callFrom')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<p class="cursor-pointer" @click="sortBy('callTo')">
								Call To <span :class="getSortIcon('callTo')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-md-1 col-lg-1 hideTablet">
							<p class="cursor-pointer" @click="sortBy('duration')">
								Duration <span :class="getSortIcon('duration')" class="ml-1"></span>
							</p>
						</div>
						<div
							v-if="!$global.hasUserRole(userRoles, 'account')"
							class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape"
						>
							<p class="cursor-pointer" @click="sortBy('carrierName')">
								Carrier <span :class="getSortIcon('carrierName')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape">
							<p class="cursor-pointer" @click="sortBy('zone_name')">
								Zone Name <span :class="getSortIcon('zone_name')" class="ml-1"></span>
							</p>
						</div>
						<div
							v-if="tenants.length > 1 || $global.hasUserRole(userRoles, 'reseller')"
							class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape"
						>
							<p class="cursor-pointer" @click="sortBy('tenantCost')">
								Tenant Cost <span :class="getSortIcon('tenantCost')" class="ml-1"></span>
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape">
							<p class="cursor-pointer" @click="sortBy('accountCost')">
								Account Cost <span :class="getSortIcon('accountCost')" class="ml-1"></span>
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="calls.length > 0" class="flex flex-col gap-3 py-2 sm:hidden">
			<div
				v-for="call in sortedCalls"
				:key="call.id"
				class="grid grid-cols-1 divide-y rounded-md border-2 bg-white px-4 py-3 text-black hover:bg-white/50 dark:border-offmaindark/80 dark:bg-offmaindark dark:text-white dark:hover:bg-offmaindark/50"
				@click.prevent="showMoreCallInfo(call)"
			>
				<div class="flex justify-between py-1 text-xs">
					<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Start Time:</p>
					<p>{{ $global.dateTimeFormat(call.startTime) }}</p>
				</div>
				<div class="flex justify-between py-1 text-xs">
					<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Account:</p>
					<p>{{ call.accountName || 'Unknown' }}</p>
				</div>
				<div class="grid grid-cols-3 justify-between py-1 text-xs">
					<p class="font-bold">{{ call.callFrom }}</p>
					<span class="fa-light fa-arrow-long-right mx-auto mt-1"></span>
					<p class="text-end font-bold">{{ call.callTo }}</p>
				</div>
				<div class="flex justify-between py-1 text-xs">
					<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Duration:</p>
					<p>{{ formatDuration(call.connectedDuration) || 0 }}s</p>
				</div>
				<transition-expand>
					<div v-if="openCalls[call.id]" class="divide-y">
						<div
							v-if="!$global.hasUserRole(userRoles, 'account')"
							class="flex justify-between py-1 text-xs"
						>
							<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Carrier:</p>
							<p class="truncate">{{ call.carrierName || '' }}</p>
						</div>
						<div class="flex justify-between py-1 text-xs">
							<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Zone Name:</p>
							<p class="truncate">{{ call.zone_name || 'N/A' }}</p>
						</div>
						<div class="flex justify-between py-1 text-xs">
							<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Tenant Cost:</p>
							<p>£{{ call.tenantBilling?.currentBilled?.toFixed(2) || '0.00' }}</p>
						</div>
						<div class="flex justify-between py-1 text-xs">
							<p class="font-semibold uppercase text-gray-500 dark:text-gray-400">Account Cost:</p>
							<p>£{{ call.accountBilling?.currentBilled?.toFixed(2) || '0.00' }}</p>
						</div>
					</div>
				</transition-expand>
				<div v-if="$global.hasUserRole(userRoles, 'realtime:clearcall')" class="py-2">
					<dropdown-menu class="dropdownTextLeft dropdownMed" :items="createMenuItems(call)">
						<template #image>
							<button
								class="flex h-6 w-full flex-row items-center justify-center rounded-sm bg-blue-500 px-4 text-white hover:bg-blue-600"
								style="color: white"
								aria-label="Action Button"
							>
								<span class="m-0 flex flex-row items-center justify-center">
									<p class="flex sm:hidden lg:flex">Action</p>
									<span class="fa-light fa-chevron-down ml-2 sm:m-0 lg:ml-2"></span>
								</span>
							</button>
						</template>
					</dropdown-menu>
				</div>
			</div>
		</div>
		<div v-for="call in sortedCalls" :key="call.id" class="card listItem hidden sm:block">
			<div class="row content middle-sm middle-md middle-lg">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
					<div class="row middle-sm middle-md middle-lg">
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-2">
							<span class="block text-xs font-semibold uppercase text-[#999999] sm:hidden"
								>Start Time:
							</span>
							<p
								class="mb-4 truncate whitespace-nowrap sm:mb-0"
								@mouseover="createTooltip($event, $global.dateTimeFormat(call.startTime))"
							>
								{{ $global.dateTimeFormat(call.startTime) }}
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<p
								class="truncate whitespace-nowrap"
								@mouseover="createTooltip($event, call.accountName || 'Unknown')"
							>
								{{ call.accountName || 'Unknown' }}
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<span class="block text-xs font-semibold uppercase text-[#999999] sm:hidden"
								>Call From:
							</span>
							<p class="mb-4 truncate sm:mb-0" @mouseover="createTooltip($event, call.callFrom)">
								{{ call.callFrom }}
							</p>
						</div>
						<div class="col-xs-12 col-sm-2 col-md-2 col-lg-1">
							<span class="block text-xs font-semibold uppercase text-[#999999] sm:hidden"
								>Call To:
							</span>
							<p class="mb-4 truncate sm:mb-0" @mouseover="createTooltip($event, call.callTo)">
								{{ call.callTo }}
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet">
							<p
								class="truncate"
								@mouseover="createTooltip($event, formatDuration(call.connectedDuration) || 0)"
							>
								{{ formatDuration(call.connectedDuration) || 0 }}
							</p>
						</div>
						<div
							v-if="!$global.hasUserRole(userRoles, 'account')"
							class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape"
						>
							<p class="truncate" @mouseover="createTooltip($event, call.carrierName || '')">
								{{ call.carrierName || '' }}
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape">
							<p class="truncate" @mouseover="createTooltip($event, call.zone_name || 'N/A')">
								{{ call.zone_name || 'N/A' }}
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape">
							<p
								class="truncate"
								@mouseover="
									createTooltip($event, '£' + call.tenantBilling?.currentBilled?.toFixed(2) || '0.00')
								"
							>
								£{{ call.tenantBilling?.currentBilled?.toFixed(2) || '0.00' }}
							</p>
						</div>
						<div class="col-xs-12 col-md-2 col-lg-1 hideTablet hideTabletLandscape">
							<p
								class="truncate"
								@mouseover="
									createTooltip(
										$event,
										'£' + call.accountBilling?.currentBilled?.toFixed(2) || '0.00'
									)
								"
							>
								£{{ call.accountBilling?.currentBilled?.toFixed(2) || '0.00' }}
							</p>
						</div>
						<div
							v-if="$global.hasUserRole(userRoles, 'realtime:clearcall')"
							class="col-sm-1 col-md-1 col-lg-1 end-sm end-md end-lg absolute bottom-4 right-4 sm:bottom-auto md:right-0"
						>
							<dropdown-menu class="dropdownTextLeft dropdownMed" :items="createMenuItems(call)">
								<template #image>
									<button
										class="flex h-6 w-full flex-row items-center justify-center rounded-sm bg-blue-500 px-4 text-white hover:bg-blue-600"
										style="color: white"
										aria-label="Action Button"
									>
										<span class="m-0 flex flex-row items-center justify-center">
											<p class="flex sm:hidden lg:flex">Action</p>
											<span class="fa-light fa-chevron-down ml-2 sm:m-0 lg:ml-2"></span>
										</span>
									</button>
								</template>
							</dropdown-menu>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="!calls || !calls.length" class="card no-action listItem orange">
			<div class="row content middle-sm middle-md middle-lg">
				<div class="col-xs-12 col-sm-12 col-md-12 col-lg-12">
					<div class="row middle-sm middle-md middle-lg">
						<div class="col-xs-12">
							<p>No calls found.</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { router } from '../router'
import CallFlowModal from '../components/CallFlowModal.vue'
import CIPFlowModal from './CIPFlowModal.vue'
import moment from 'moment'
import 'moment-duration-format'

export default {
	components: {
		CallFlowModal,
		CIPFlowModal
	},
	inject: ['user', 'userRoles', 'tenants'],
	props: ['calls'],

	data() {
		return {
			selectedSIPCapture: null,
			selectedCallData: null,
			selectedFlow: null,
			openCalls: {},
			lastCallData: null,
			moment,
			sortField: 'duration',
			sortDirection: 'desc'
		}
	},

	computed: {
		selectedFlowData() {
			const call = this.calls.find((call) => call.id === this.selectedFlow)
			if (call) {
				this.setLastSelectedFlow(call)
				return call
			} else {
				return this.lastCallData
			}
		},
		selectedFlowIsCompleted() {
			return !this.calls.find((call) => call.id === this.selectedFlow)
		},
		sortedCalls() {
			return [...this.calls].sort((a, b) => {
				let comparison = 0
				switch (this.sortField) {
					case 'startTime':
						comparison = a.startTime - b.startTime
						break
					case 'accountName':
						comparison = (a.accountName || '').localeCompare(b.accountName || '')
						break
					case 'callFrom':
						comparison = a.callFrom.localeCompare(b.callFrom)
						break
					case 'callTo':
						comparison = a.callTo.localeCompare(b.callTo)
						break
					case 'duration':
						comparison = (a.connectedDuration || 0) - (b.connectedDuration || 0)
						break
					case 'carrierName':
						comparison = (a.carrierName || '').localeCompare(b.carrierName || '')
						break
					case 'zone_name':
						comparison = (a.zone_name || '').localeCompare(b.zone_name || '')
						break
					case 'tenantCost':
						comparison = (a.tenantBilling?.currentBilled || 0) - (b.tenantBilling?.currentBilled || 0)
						break
					case 'accountCost':
						comparison = (a.accountBilling?.currentBilled || 0) - (b.accountBilling?.currentBilled || 0)
						break
				}
				return this.sortDirection === 'asc' ? comparison : -comparison
			})
		}
	},

	methods: {
		setLastSelectedFlow(call) {
			if (call) this.lastCallData = JSON.parse(JSON.stringify(call))
		},
		createMenuItems(call) {
			let items = [
				{
					handler: () => {
						this.clearCall(call)
					},
					icon: 'fa-light fa-delete-left',
					label: 'Clear call'
				},
				{
					handler: () => {
						this.showSIPCapture(call)
					},
					icon: 'fa-light fa-phone-arrow-right',
					label: 'SIP Call Flow'
				}
			]

			if (call.flowHistory?.flows?.length > 0) {
				items.push({
					handler: () => {
						this.showFlowHistory(call)
					},
					icon: 'fa-kit fa-teleflow',
					label: 'Flow Viewer'
				})
			}

			return items
		},
		showSIPCapture(call) {
			this.selectedSIPCapture = call.id
			this.selectedCallData = call
		},
		showFlowHistory(call) {
			this.selectedFlow = call.id
		},
		showMoreCallInfo(call) {
			if (this.openCalls[call.id]) {
				delete this.openCalls[call.id]
			} else {
				this.openCalls[call.id] = true
			}
		},
		formatDuration(seconds) {
			return moment.duration(seconds, 'seconds').format('HH:mm:ss', { trim: false })
		},
		logCalls() {
			console.log(this.calls)
		},
		clearCall(call) {
			const tenantBillingId = call.tenantBilling?.id
			const accountBillingId = call.accountBilling?.id

			this.$emit('clearCall', {
				tenantBillingId: tenantBillingId,
				accountBillingId: accountBillingId,
				callId: call.id,
				nodeIp: call.nodeIp
			})
		},
		routeTo(routeName) {
			router.push(routeName)
		},
		sortBy(field) {
			if (this.sortField === field) {
				this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
			} else {
				this.sortField = field
				this.sortDirection = 'asc'
			}
		},
		getSortIcon(field) {
			if (this.sortField !== field) return ''
			return this.sortDirection === 'asc' ? 'fa-light fa-chevron-up' : 'fa-light fa-chevron-down'
		}
	}
}
</script>
